import { __rest } from 'tslib';
import React from 'react';
import { Box } from '../../grid/Box/styled.js';
import { Text } from '../../typography/Text/Text.js';
import { useTheme } from '../../theme/Theme.js';
import { Button } from '../Button/Button.js';
import { Title } from '../../typography/Title/Title.js';
import { Close } from '../../icons/Close/Close.js';
import { useTranslation } from '../../locale/useTranslation.js';
import { Tooltip } from '../Tooltip/Tooltip.js';
import { Root } from './styled.js';
import { useSize } from './hooks.js';

const CloseIcon = () => {
    const t = useTranslation();
    return (React.createElement(Tooltip, { title: t('general.close') },
        React.createElement(Close, { color: 'gray650', size: 20 })));
};
const Drawer = (_a) => {
    var { fullscreen = false, above, before, title, description, size = 'medium', placement = 'right', okButtonProps, onOk, okText = 'Принять', cancelButtonProps, onCancel, cancelText = 'Отмена', footer, closeIcon = React.createElement(CloseIcon, null) } = _a, props = __rest(_a, ["fullscreen", "above", "before", "title", "description", "size", "placement", "okButtonProps", "onOk", "okText", "cancelButtonProps", "onCancel", "cancelText", "footer", "closeIcon"]);
    if (process.env.NODE_ENV === 'development') {
        if (size === 'default') {
            console.warn(`[Drawer]: Drawer размера "${size}" устарел и более не поддерживается. Используйте 'size="medium". За более подробной информацией обратитесь в S.TendUI Support чат`);
        }
    }
    const theme = useTheme();
    const _size = useSize(size);
    const isTop = placement === 'top';
    const isBottom = placement === 'bottom';
    const isLeft = placement === 'left';
    const isRight = placement === 'right';
    const isVertical = isTop || isBottom;
    const isHorizontal = isLeft || isRight;
    const _title = React.useMemo(() => {
        if ([!title, !description, !before, !above].every(Boolean))
            return;
        if (above)
            return (React.createElement(Box, null,
                above,
                React.createElement(Box, { "$display": 'flex', "$gap": 12 },
                    before && React.createElement(Box, null, before),
                    React.createElement(Box, { "$display": 'flex', "$flexDirection": 'column' },
                        title && (React.createElement(Title, { margin: '0', level: 'h5' }, title)),
                        description && (React.createElement(Text, { color: 'gray650', fontWeight: '400', size: 'small' }, description))))));
        return (React.createElement(Box, { "$display": 'flex', "$gap": 12 },
            before && React.createElement(Box, null, before),
            (title || description) && (React.createElement(Box, { "$display": 'flex', "$flexDirection": 'column' },
                title && (React.createElement(Title, { margin: '0', level: 'h5' }, title)),
                description && (React.createElement(Text, { color: 'gray650', fontWeight: '400', size: 'small' }, description))))));
    }, [above, before, description, title]);
    const _footer = React.useMemo(() => {
        var _a, _b;
        if (typeof footer === 'undefined') {
            return (React.createElement(Box, { "$width": '100%', "$display": 'inline-flex', "$alignItems": 'center', "$justifyContent": 'flex-end', "$gap": 8 },
                React.createElement(Button, Object.assign({ "data-testid": 'tend-ui-drawer-cancel-button', variant: 'secondary' }, cancelButtonProps, { onClick: (_a = cancelButtonProps === null || cancelButtonProps === void 0 ? void 0 : cancelButtonProps.onClick) !== null && _a !== void 0 ? _a : onCancel }), (cancelButtonProps === null || cancelButtonProps === void 0 ? void 0 : cancelButtonProps.children) || cancelText),
                React.createElement(Button, Object.assign({ "data-testid": 'tend-ui-drawer-ok-button' }, okButtonProps, { onClick: (_b = okButtonProps === null || okButtonProps === void 0 ? void 0 : okButtonProps.onClick) !== null && _b !== void 0 ? _b : onOk }), (okButtonProps === null || okButtonProps === void 0 ? void 0 : okButtonProps.children) || okText)));
        }
        return footer;
    }, [cancelButtonProps, cancelText, footer, okButtonProps, okText, onCancel, onOk]);
    const headerStyle = React.useMemo(() => (Object.assign(Object.assign({}, props.headerStyle), { borderBottom: 'none' })), [props.headerStyle]);
    const bodyStyle = React.useMemo(() => (Object.assign(Object.assign({}, props.bodyStyle), { padding: '0 24px' })), [props.bodyStyle]);
    const footerStyle = React.useMemo(() => (Object.assign(Object.assign({}, props.footerStyle), { borderTop: 'none', padding: '16px 24px' })), [props.footerStyle]);
    const contentWrapperStyle = React.useMemo(() => ({
        top: Object.assign(Object.assign({}, props.contentWrapperStyle), { overflow: 'hidden' }),
        right: Object.assign(Object.assign({}, props.contentWrapperStyle), { overflow: 'hidden' }),
        bottom: Object.assign(Object.assign({}, props.contentWrapperStyle), { overflow: 'hidden' }),
        left: Object.assign(Object.assign({}, props.contentWrapperStyle), { overflow: 'hidden' }),
    }[placement]), [placement, props.contentWrapperStyle]);
    const width = (() => {
        if (fullscreen && isHorizontal)
            return '100%';
        if (props.width)
            return props.width;
        return {
            default: '500px',
            small: '400px',
            medium: '500px',
            large: '800px',
        }[size];
    })();
    const height = (() => {
        if (fullscreen && isVertical)
            return '100%';
        return props.height;
    })();
    const styles = React.useMemo(() => (Object.assign(Object.assign({}, props.styles), { header: headerStyle, body: bodyStyle, footer: footerStyle })), [bodyStyle, footerStyle, headerStyle, props.styles]);
    return (React.createElement(Root, Object.assign({ "data-testid": 'tend-ui-drawer' }, props, { "$theme": theme, styles: styles, contentWrapperStyle: contentWrapperStyle, title: _title, footer: _footer, placement: placement, width: width, height: height, size: _size, closeIcon: closeIcon })));
};
Drawer.displayName = 'Drawer';

export { Drawer };
//# sourceMappingURL=Drawer.js.map
